import { createListenerMiddleware } from '@reduxjs/toolkit';
import { ProjectRenderer } from '@/renderer/ProjectRenderer';
import { creatorActions } from '../slices/creatorSlice';
import { RootState, AppDispatch } from '..';
import { RendererMode } from '@/renderer/types';
import { CreatorOptions, CreatorStep, HistoryAction } from '@/types/creator';
import { computeLevelsQuantity } from '@/utils/utilities';
import { clamp } from 'lodash';
import APP_CONFIG from '@/configs/appConfig';
import shapes from '@/configs/shapes';

const renderer = ProjectRenderer.getInstance();

function syncStateToRendererMode(options: CreatorOptions) {
	if (options.isPreviewMode) {
		renderer.mode = RendererMode.PREVIEW;
		return;
	}

	if (
		options.coloringOptions.popoverOpened &&
		options.coloringOptions.selectedAttributeValue
	) {
		renderer.mode = RendererMode.COLORING;
		return;
	}

	if (options.isMeasuring) {
		renderer.mode = RendererMode.MEASURING;
		return;
	}

	if (options.isDrawing) {
		renderer.mode = RendererMode.CUSTOM_SHAPE_DRAWING;
		return;
	}

	switch (options.currentStep) {
		case CreatorStep.SHAPES:
			renderer.mode = RendererMode.ROOM;
			break;
		case CreatorStep.MODULES:
			renderer.mode = RendererMode.MODULES;
			break;
		default:
			renderer.mode = RendererMode.PREVIEW;
			break;
	}
}

export const creatorListenerMiddleware = createListenerMiddleware();

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.selectShape,
	effect: ({ payload }, listenerApi) => {
		if (payload === 'custom') {
			renderer.drawingManager.initDrawingShapeMode();
			return;
		}

		const shape = shapes.find((shape) => shape.id === payload);
		if (!shape) return;

		renderer.roomManager.setFromShape(shape.vertices);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setCreatorStep,
	effect: ({ payload }, listenerApi) => {
		const { currentStep } = listenerApi.getState().creator.options;

		if (currentStep === CreatorStep.MODULES)
			listenerApi.dispatch(
				creatorActions.updateOptions({
					largestAvailableRectangle:
						renderer.roomManager.getLargestAvailableRectangle(),
				})
			);

		renderer.handleCreatorStepChange(currentStep);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.enterPresetMode,
	effect: async ({ payload }, listenerApi) => {
		const { currentStep } = listenerApi.getState().creator.options;

		renderer.handleCreatorStepChange(currentStep);

		if (payload) await renderer.loadFromPreset(payload.options.rendererModules);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setWallsVisibility,
	effect: ({ payload }, listenerApi) => {
		renderer.roomManager.wallsVisible = payload;
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setActiveCamera,
	effect: ({ payload }, listenerApi) => {
		renderer.cameraController.activeCamera = payload;
		syncStateToRendererMode(listenerApi.getState().creator.options);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setRoomDragType,
	effect: ({ payload }, listenerApi) => {
		renderer.roomManager.roomDragType = payload;
		syncStateToRendererMode(listenerApi.getState().creator.options);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.addCreatorProductModule,
	effect: async ({ payload }, listenerApi) => {
		await renderer.modulesManager.addProductModule(
			payload.refId,
			payload.productModule.asset
		);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.removeCreatorProductModule,
	effect: async ({ payload }, listenerApi) => {
		await renderer.modulesManager.removeProductModule(payload);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.addCreatorLandscapeModule,
	effect: async ({ payload }, listenerApi) => {
		await renderer.modulesManager.addLandscapeModule(
			payload.refId,
			payload.landscapeModule.adjustableToWalls,
			payload.landscapeModule.asset
		);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.removeCreatorLandscapeModule,
	effect: async ({ payload }, listenerApi) => {
		await renderer.modulesManager.removeLandscapeModule(payload);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.loadProject,
	effect: async ({ payload }, listenerApi) => {
		if (payload.options.state.options.isPreviewMode)
			renderer.mode = RendererMode.PREVIEW;

		await renderer.loadFromProject(payload.options.renderer);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.loadFromPreset,
	effect: async ({ payload }, listenerApi) => {
		await renderer.loadFromPreset(payload.options.rendererModules);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.copyProject,
	effect: async ({ payload }, listenerApi) => {
		renderer.mode = RendererMode.ROOM;
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setCurrentLevel,
	effect: async ({ payload }, listenerApi) => {
		renderer.currentLevel = payload;
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.generateRandomAttributeValues,
	effect: async ({ payload }, listenerApi) => {
		const colors = payload.attributeValues.map(({ value }) => value);
		renderer.modulesManager.coloringManager.setRandomColors(colors);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.handleHistoryAction,
	effect: async ({ payload }) => {
		switch (payload.action) {
			case HistoryAction.UNDO:
				await renderer.modulesManager.historyManager.undo();
				break;
			case HistoryAction.REDO:
				await renderer.modulesManager.historyManager.redo();
				break;
		}
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setRoomHeight,
	effect: async ({ payload }, listenerApi) => {
		listenerApi.cancelActiveListeners();
		await listenerApi.delay(500);

		const { levelsQuantity } = listenerApi.getState().creator.renderer;

		const clamped =
			clamp(payload, APP_CONFIG.ROOM_HEIGHT.MIN, APP_CONFIG.ROOM_HEIGHT.MAX) /
			100;

		renderer.roomManager.roomHeight = clamped;

		const updatedLevelsQuantity = computeLevelsQuantity(clamped);

		if (updatedLevelsQuantity < levelsQuantity)
			listenerApi.dispatch(creatorActions.setCurrentLevel(0));

		if (updatedLevelsQuantity !== levelsQuantity)
			listenerApi.dispatch(
				creatorActions.updateRenderer({
					levelsQuantity: updatedLevelsQuantity,
				})
			);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setMeasuring,
	effect: ({ payload }, listenerApi) => {
		syncStateToRendererMode(listenerApi.getState().creator.options);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.updateColoringOptions,
	effect: ({ payload }, listenerApi) => {
		const selectedValue =
			listenerApi.getState().creator.options.coloringOptions
				.selectedAttributeValue;

		syncStateToRendererMode(listenerApi.getState().creator.options);
		renderer.modulesManager.coloringManager.selectedColor = selectedValue
			? selectedValue.value
			: null;
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.removeMeasurement,
	effect: async ({ payload }, listenerApi) => {
		renderer.measurementManager.handleRemoveMeasure(payload);
	},
});
